import { CommonModule } from '@angular/common';
import {
  Component,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
  inject,
} from '@angular/core';
import { RouterModule } from '@angular/router';
import { firstValueFrom, lastValueFrom, Subject, Subscription, take, takeUntil } from 'rxjs';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { fader } from '../app/app.animations';
import { AppGlobalService } from '../app/commons/services/app-global.service';
import { ToastModule } from 'primeng/toast';
import { ConfirmationService, MessageService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { UiUtilsService } from '../app/commons/services/UiUtils.service';
import { AuthService } from '../app/auth/auth.service';
import { Company, CustomMenuItem, LanguageCode, User } from '@job-v2/ats-utils';
import { NgxSpinnerModule } from 'ngx-spinner';
import {
  AsideGroup,
  AsideParams,
  HeaderCompany,
  HeaderParams,
  MainComponent,
  Roles,
  SociumApp,
  StateStoreKeys,
  StatesService,
  UserRole,
  Company as CompanyLib,
  UserRolesPermissionsService,
  HeaderUser
} from '@socium/sociumflow';
import { Company as _company  } from '@socium/common-api';
import { TranslateService } from '@ngx-translate/core';
import { adminMenu, managerMenu, recruiterMenu } from './sidebar/sidebar-menu';
import { CompaniesService } from '../app/settings/companies/companies.service';
import { ToolBarComponent } from './toolbar/toolbar.component';
import { LanguageService } from '../app/commons/services/language.service';
import { environment } from '../environments/environment';
import { ConfirmDialogModule } from 'primeng/confirmdialog';

@Component({
  selector: 'app-layout',
  standalone: true,
  imports: [
    CommonModule,
    ProgressSpinnerModule,
    RouterModule,
    ToastModule,
    ConfirmDialogModule,
    NgxSpinnerModule,
    MainComponent,
    ToolBarComponent,
  ],
  templateUrl: './layouts.component.html',
  styleUrls: ['./layouts.component.scss'],
  providers: [
    AuthService,
    ToolBarComponent,
  ],
  encapsulation: ViewEncapsulation.None,
  animations: [fader],
})
export class LayoutComponent implements OnInit, OnDestroy {
  isCollapsed = false;
  private readonly appGlobalService = inject(AppGlobalService);
  private readonly companiesService = inject(CompaniesService);
  private readonly translate = inject(TranslateService);
  private readonly authService = inject(AuthService);
  private  readonly language = inject(LanguageService);
  private readonly statesService = inject(StatesService);
  private readonly userRolesPermissionsService = inject(UserRolesPermissionsService);
  private readonly dialogService = inject(DialogService);
  company = this.authService.getCompanyFromLocalStorage();
  subscription = new Subscription();
  companies: Company[] = [];
  user: User | null = null;
  destroy$ = new Subject<void>();
  appsParams = {
    displayApps: true,
    activeApp: SociumApp.JOB,
  };
  selectedCompanyId = '';
  headerParams: HeaderParams = {
    companies: [],
    selectedCompany: null,
    user: {
      id: '',
      firstName: '',
      lastName: '',
      isAdmin: null,
      avatar: null,
      role: '',
    },
    displaySearch: false,
    displayDropdown:
      this.userRolesPermissionsService.hasUserAccessToMultipleCompanies(),
    hasNotifications: false,
    hideLangSelector: false,
    hideNotifications: true,
    isLoading: false,
  };

  asideParams: AsideParams = {
    app: SociumApp.JOB,
    groups: [],
    isCollapsed: false,
  };

  ngOnInit(): void {
    this.subscription.add(
      this.statesService.getCurrentUser$()
      .pipe(take(1))
      .subscribe((userLib) => {
        const currentUser = userLib as User;
        if (currentUser) {
          this.setUserInHeaderParams(currentUser);
          this.handleFnCallByRole(currentUser);
        }
      }),
    );
    this.subscription.add(
      this.statesService.getSelectedCompany$()
      .pipe(take(1))
      .subscribe((companyLib) => {
        const company = companyLib as CompanyLib;
        this.company = company as unknown as Company;
        if (company) {
          this.selectedCompanyId = company._id || company.id || '';
          const isCompanyHoldingString =
            typeof company.companyHolding === 'string';
          this.headerParams = {
            ...this.headerParams,
            selectedCompany: {
              id: company.id || '',
              name: company.name ?? '',
              logo: company.logo,
              companyHolding:
                (isCompanyHoldingString
                  ? (company.companyHolding as string)
                  : (company.companyHolding as unknown as Company)?._id) || company?._id,
            },
          };
          this.intializeMenu();
        }
      }),
    );
  }

  constructor() {
    this.authService.getAuthenticatedUser();
  }

  intializeMenu() {
    let items: CustomMenuItem[] = [];
    this.appGlobalService.currentUser$
    .pipe(take(1))
    .subscribe((value) => {
      if (value) {
        this.user = value
        this.authService.defineUserRole();
        if (this.authService.isAdmin || this.authService.isSociumAdmin) items = adminMenu;
        else if (this.authService.isManager) items = managerMenu;
        else items = recruiterMenu;

        this.asideParams.groups = items.map((elt) => ({
          label: this.translate.instant(elt.label || ''),
          iconClass: elt.icon,
          redirectUrl: elt.routerLink,
          isActive: true,
          isEnabled: true,
          isClosed: false,
          items: elt.items?.map((elt) => ({
            label: this.translate.instant(elt.label || ''),
            iconClass: elt.icon,
            redirectUrl: elt.routerLink,
            isEnabled: true,
          })),
        })) as AsideGroup[];
      }
      this.company = this.authService.getCompanyFromLocalStorage();
    });
  }

  handleSelectCompany(company: _company) {
    if (this.dialogService.dialogComponentRefMap) {
      this.dialogService.dialogComponentRefMap.forEach(dialog => dialog.destroy());
    }
    const selectedCompanyId = company?.id || company?._id || '';
    this.statesService.setCookie<string>(StateStoreKeys.SELECTED_COMPANY_ID, selectedCompanyId)
    this.statesService.setSelectedCompany(company as unknown as CompanyLib);
    this.selectedCompanyId = selectedCompanyId;
    this.intializeMenu();
    this.authService.updateCompanyActive(company as unknown as Company)
  }

  public handleNavigateToUserProfile(): void {
    if (this.user) {
      const path = `${environment.workspaceUrl}/management/profile/${this.user._id}`;
      window.open(path);
    }
  }

  public logout(): void {
    this.authService.logout();
  }

  public onLanguageChange(lang: string) {
    const selectedLang =
      lang == LanguageCode.FR.toUpperCase() ? LanguageCode.FR : LanguageCode.EN;
    this.translate.use(selectedLang);
    this.language.saveChangeLang(selectedLang.toLowerCase());
    window.location.reload()
  }

  handleFnCallByRole(user: User) {
    switch (true) {
      case this.userRolesPermissionsService.hasSociumManagementAccess():
        this.headerParams.fromNative = true;
        this.headerParams.user.isAdmin = true;
        this.headerParams.displayDropdown = true;
        break;
      case user.role && user.role === UserRole.HOLDING_ADMIN:
        this.headerParams.fromNative = true;
        this.headerParams.user.isAdmin = true;
        this.headerParams.displayDropdown = true;
        break;
      case user.role && user.role === UserRole.HR_SUPER_ADMIN:
        this.headerParams.fromNative = false;
        break;
      default:
        this.headerParams.fromNative = false;
        break;
    }
  }

  setUserInHeaderParams(currentUser: User) {
    this.headerParams = {
      ...this.headerParams,
      user: {
        id: currentUser.id || '',
        firstName: currentUser?.firstName || '',
        lastName: currentUser?.lastName || '',
        isAdmin: null,
        avatar: currentUser?.avatar || null,
        role: currentUser?.role || '',
      } as HeaderUser,
    };
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }
}
